import type { AccessQueueItemPublic, UserPublic, UsersSummary as Summary } from '@accounts/shared';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { STR } from '../../../i18n';
import { getAccessQueue, listAudit } from '../../../lib/api';
import { formatDateTime } from '../../../lib/format';
import { accessQueueScopeOf, queryKeys } from '../../../lib/queryKeys';
import { useSession } from '../../../lib/session';
import { Card, CardHeader, Chip, EmptyRow, Tile } from '../../../ui/Primitives';
import { monoStyle } from '../../../ui/styles';

/**
 * THE USERS SUMMARY (FINAL demo, users option 1): "the list stays; the right pane is a
 * summary until you pick someone". The Users screen OPENS on this pane (spec row 4); picking
 * a person shows their page with every existing control, and the Summary control above the
 * list brings this pane back.
 *
 * Totals come from `meta.summary` (the whole directory) except HR, which the summary does
 * not count — that one is read from the loaded list and says so when the list is capped.
 * Open access work is the Queue, grouped by person. Recent changes are audit rows, which only
 * administrators can read, so HR does not get that card.
 */
export function UsersSummary({
  summary,
  users,
  truncated,
  hrOnly,
  onOpen,
}: {
  summary: Summary | null;
  users: readonly UserPublic[];
  /** The loaded list is shorter than the directory (LIST_LIMIT). */
  truncated: boolean;
  hrOnly: boolean;
  onOpen: (userId: string) => void;
}) {
  const session = useSession();
  const queueQuery = useQuery({
    // Keyed by the actor's scope, like every Queue read (see `queryKeys.accessQueue`).
    queryKey: queryKeys.accessQueue(
      session.data === undefined ? 'limited' : accessQueueScopeOf(session.data),
      {},
    ),
    queryFn: () => getAccessQueue(),
    enabled: session.data !== undefined,
  });
  // For HR the server's Queue already holds only HR's rows (SIR-7591); nothing is cut here.
  const recentQuery = useQuery({
    queryKey: [...queryKeys.accessAll, 'recent-changes'] as const,
    queryFn: () => listAudit({ group: 'access', limit: 9 }),
    enabled: !hrOnly,
  });

  const names = useMemo(() => new Map(users.map((user) => [user.id, user.name])), [users]);

  const openByPerson = useMemo(() => {
    const items: readonly AccessQueueItemPublic[] = queueQuery.data?.data ?? [];
    const byPerson = new Map<string, { id: string; name: string; count: number; overdue: boolean }>();
    for (const item of items) {
      const entry = byPerson.get(item.user.id) ?? {
        id: item.user.id,
        name: item.user.name,
        count: 0,
        overdue: false,
      };
      entry.count += 1;
      entry.overdue = entry.overdue || item.overdue;
      byPerson.set(item.user.id, entry);
    }
    return [...byPerson.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [queueQuery.data]);

  const hrCount = users.filter((user) => user.isHr).length;
  const itCount = users.filter((user) => user.isIt).length;
  const recent = recentQuery.data?.data ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 980 }}>
      <div>
        <div style={{ fontSize: 19, fontWeight: 650, letterSpacing: '-.015em' }}>
          {STR.access.person.summaryTitle}
        </div>
        <div className="tx-subtle" style={{ fontSize: 13, marginTop: 2 }}>
          {STR.access.person.summaryLead}
        </div>
        <div className="tx-subtle" style={{ fontSize: 12, marginTop: 6 }}>
          {STR.access.person.summarySearchHint}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12 }}>
        <Tile label={STR.access.person.totals.active}>{summary?.active ?? STR.common.dash}</Tile>
        <Tile label={STR.access.person.totals.deactivated}>{summary?.deactivated ?? STR.common.dash}</Tile>
        <Tile label={STR.access.person.totals.admins}>{summary?.admins ?? STR.common.dash}</Tile>
        <Tile label={STR.access.person.totals.hr}>
          {hrCount}
          {truncated ? (
            <span className="tx-subtle" style={{ display: 'block', fontSize: 11, fontWeight: 500 }}>
              {STR.access.person.hrCountCapped}
            </span>
          ) : null}
        </Tile>
        <Tile label={STR.access.person.totals.it}>
          {itCount}
          {truncated ? (
            <span className="tx-subtle" style={{ display: 'block', fontSize: 11, fontWeight: 500 }}>
              {STR.access.person.hrCountCapped}
            </span>
          ) : null}
        </Tile>
        <Tile label={STR.access.person.totals.open}>{openByPerson.length}</Tile>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: hrOnly ? 'minmax(0,1fr)' : 'minmax(0,1.2fr) minmax(0,1fr)',
          gap: 16,
          alignItems: 'start',
        }}
      >
        {hrOnly ? null : (
          <Card>
            <CardHeader
              title={STR.access.person.recentTitle}
              aside={
                <span className="tx-subtle" style={{ fontSize: 12, fontWeight: 500 }}>
                  {STR.access.person.recentAside}
                </span>
              }
            />
            {recent.length === 0 ? (
              <EmptyRow>{STR.access.person.recentEmpty}</EmptyRow>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {recent.map((entry) => {
                  const rawUserId = entry.metadata?.userId;
                  const personId = typeof rawUserId === 'string' ? rawUserId : null;
                  const person = personId === null ? undefined : names.get(personId);
                  const body = (
                    <>
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                          {person === undefined ? null : <b style={{ fontWeight: 650 }}>{person}</b>}
                          <Chip tone="neutral" size="sm" style={monoStyle}>
                            {entry.action}
                          </Chip>
                        </span>
                        <span className="tx-subtle" style={{ display: 'block', fontSize: 11.5 }}>
                          {STR.access.person.recentBy(entry.actor?.name ?? STR.common.dash)}
                        </span>
                      </span>
                      <span className="tx-subtle" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
                        {formatDateTime(entry.createdAt)}
                      </span>
                    </>
                  );
                  const rowStyle = {
                    width: '100%',
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0,1fr) auto',
                    gap: 12,
                    alignItems: 'center',
                    padding: '10px 18px',
                    border: 'none',
                    borderRadius: 0,
                    textAlign: 'left' as const,
                    color: 'var(--text)',
                    background: 'none',
                  };
                  return (
                    <li key={entry.id} className="row" style={{ borderBottom: '1px solid var(--border)' }}>
                      {personId === null || person === undefined ? (
                        <div style={rowStyle}>{body}</div>
                      ) : (
                        <button
                          type="button"
                          className="tbtn"
                          style={{ ...rowStyle, cursor: 'pointer' }}
                          onClick={() => {
                            onOpen(personId);
                          }}
                        >
                          {body}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        )}

        <Card>
          <CardHeader
            title={STR.access.person.openWorkTitle}
            aside={
              <span className="tx-subtle" style={{ fontSize: 12, fontWeight: 500 }}>
                {STR.access.person.openWorkAside}
              </span>
            }
          />
          {openByPerson.length === 0 ? (
            <EmptyRow>{STR.access.person.openWorkEmpty}</EmptyRow>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {openByPerson.map((entry) => (
                <li key={entry.id} className="row" style={{ borderBottom: '1px solid var(--border)' }}>
                  <button
                    type="button"
                    className="tbtn"
                    onClick={() => {
                      onOpen(entry.id);
                    }}
                    style={{
                      width: '100%',
                      display: 'flex',
                      gap: 12,
                      alignItems: 'center',
                      padding: '10px 18px',
                      border: 'none',
                      borderRadius: 0,
                      textAlign: 'left',
                      color: 'var(--text)',
                      background: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 650 }}>{entry.name}</span>
                    <span
                      className={entry.overdue ? 'tx-danger-strong' : 'tx-subtle'}
                      style={{ fontSize: 11.5, fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}
                    >
                      {entry.overdue
                        ? STR.access.person.openWorkCountOverdue(entry.count)
                        : STR.access.person.openWorkCount(entry.count)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
